import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { FileText, CheckCircle, XCircle } from "lucide-react";

const ClientPortalQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchQuotes = async () => {
    try {
      const res = await API.get("/portal/quotes");
      setQuotes(res.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des devis");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleRespond = async (quoteId, action) => {
    try {
      await API.put(`/portal/quotes/${quoteId}/respond`, { action });
      toast.success(action === "accepte" ? "Devis accepté" : "Devis refusé");
      fetchQuotes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const pendingQuotes = quotes.filter((q) => q.statut === "envoye");
  const otherQuotes = quotes.filter((q) => q.statut !== "envoye");

  return (
    <div className="space-y-6" data-testid="client-portal-quotes">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Mes devis</h1>
        <p className="text-muted-foreground">Consultez et validez vos devis de réparation</p>
      </div>

      {/* Pending Quotes */}
      {pendingQuotes.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" />
            En attente de validation ({pendingQuotes.length})
          </h2>
          {pendingQuotes.map((quote) => (
            <Card key={quote.id} className="border-amber-200" data-testid={`pending-quote-${quote.id}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-semibold text-lg">{quote.numero}</p>
                    <p className="text-sm text-muted-foreground">Dossier {quote.sav_case_numero}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(quote.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  <Badge className="bg-purple-100 text-purple-800">En attente</Badge>
                </div>

                <div className="space-y-2 mb-3">
                  {quote.lignes?.map((ligne, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1">
                      <span className="text-muted-foreground">
                        {ligne.description} x{ligne.quantite}
                      </span>
                      <span className="font-medium">{ligne.total?.toFixed(2)}€</span>
                    </div>
                  ))}
                </div>

                <Separator className="my-3" />

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total TTC</p>
                    <p className="text-2xl font-bold">{quote.total_ttc?.toFixed(2)}€</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleRespond(quote.id, "refuse")}
                      data-testid={`refuse-btn-${quote.id}`}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Refuser
                    </Button>
                    <Button
                      onClick={() => handleRespond(quote.id, "accepte")}
                      data-testid={`accept-btn-${quote.id}`}
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Accepter
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Past Quotes */}
      {otherQuotes.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Historique des devis</h2>
          {otherQuotes.map((quote) => (
            <Card key={quote.id} data-testid={`past-quote-${quote.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{quote.numero}</p>
                    <p className="text-sm text-muted-foreground">
                      Dossier {quote.sav_case_numero} - {new Date(quote.created_at).toLocaleDateString("fr-FR")}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold">{quote.total_ttc?.toFixed(2)}€</span>
                    <Badge
                      className={
                        quote.statut === "accepte"
                          ? "bg-green-100 text-green-800"
                          : quote.statut === "refuse"
                          ? "bg-red-100 text-red-800"
                          : "bg-gray-100 text-gray-800"
                      }
                    >
                      {quote.statut === "accepte" ? "Accepté" : quote.statut === "refuse" ? "Refusé" : quote.statut}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {quotes.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="font-medium">Aucun devis</p>
            <p className="text-sm text-muted-foreground">Vos devis de réparation apparaîtront ici</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClientPortalQuotes;
